// src/pages/UserDashboard.js
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth, db, storage } from '../firebase';
import { signOut } from 'firebase/auth';
import {
  doc,
  getDoc,
  setDoc,
  query,
  collection,
  where,
  getDocs,
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';

// External libs
import { QRCodeCanvas } from 'qrcode.react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import heic2any from 'heic2any';
import Cropper from 'react-easy-crop';

// Styles / components
import '../styles/UserDashboard.css';
import LivePreview from '../components/LivePreview';

/* ===================== Initial profile default shape ===================== */
const initialProfileState = {
  displayName: '',
  username: '',
  profileType: 'personal',
  theme: 'light', // 'light', 'dark', 'forest', 'neon', 'sunset', 'custom'
  photoURL: '',
  isAdmin: false,
  plan: 'free',

  // supports: 'icons' | 'buttons' | 'chips' | 'list' | 'grid' | 'minimal_text' | 'qr_block'
  contactLinksDisplay: 'icons',

  contactLinks: {
    phone: '',
    email: '',
    whatsapp: '',
    instagram: '',
    facebook: '',
    tiktok: '',
    linkedin: '',
    snapchat: '',
    x: '',
  },

  customLinks: [],

  themeSettings: {
    backgroundType: 'color', // 'color' | 'gradient'
    backgroundColor: '#ffffff',
    gradientStart: '#ffffff',
    gradientEnd: '#000000',
    gradientAngle: 180,
    backgroundImageURL: '',
    buttonStyle: 'rounded', // 'rounded' | 'square' | 'outline'
    buttonColor: '#007bff',
    buttonFontColor: '#000000',
    fontFamily: 'Arial, sans-serif',

    // ---- Avatar controls ----
    avatarSize: 110,                 // px
    avatarShape: 'circle',           // 'circle' | 'rounded' | 'square'
    avatarObjectFit: 'cover',        // 'cover' | 'contain'
    avatarBorderWidth: 4,            // px
    avatarBorderColor: 'rgba(255,255,255,0.9)',
    avatarShadow: true,              // boolean
    avatarAlign: 'center',           // 'center' | 'left'

    // ---- NEW: avatar background (behind the image) ----
    avatarBgMode: 'transparent',     // 'transparent' | 'color'
    avatarBgColor: '#ffffff',
    avatarBgOpacity: 1,              // 0..1
    avatarBgApplyMode: 'onlyTransparent', // 'always' | 'onlyTransparent'

    // ---- (Optional) global page override for LivePreview (already supported there) ----
    forceBg: {
      mode: 'inherit',               // 'inherit' | 'color' | 'gradient'
      color: '#ffffff',
      gradientStart: '#4a6f7b',
      gradientEnd: '#2f4e61',
      gradientAngle: 180,
      fontFamily: 'Arial, sans-serif',
    },
  },
};

/* ===================== Migration / Normalization ===================== */
const migrateData = (rawData) => {
  const data = { ...(rawData || {}) };

  // legacy migration: `links` -> `contactLinks`
  if (
    data.links &&
    typeof data.links === 'object' &&
    !Array.isArray(data.links)
  ) {
    const oldLinks = data.links;
    delete data.links;
    data.contactLinks = {
      ...initialProfileState.contactLinks,
      ...oldLinks,
      ...(data.contactLinks || {}),
    };
    if (!Array.isArray(data.customLinks)) {
      data.customLinks = [];
    }
  }

  // deep merge: contactLinks
  const mergedContactLinks = {
    ...initialProfileState.contactLinks,
    ...(data.contactLinks || {}),
  };

  // deep merge: themeSettings
  const mergedThemeSettings = {
    ...initialProfileState.themeSettings,
    ...(data.themeSettings || {}),
  };

  // ensure avatar & new bg defaults exist
  mergedThemeSettings.avatarSize =
    mergedThemeSettings.avatarSize ?? initialProfileState.themeSettings.avatarSize;
  mergedThemeSettings.avatarShape =
    mergedThemeSettings.avatarShape || initialProfileState.themeSettings.avatarShape;
  mergedThemeSettings.avatarObjectFit =
    mergedThemeSettings.avatarObjectFit || initialProfileState.themeSettings.avatarObjectFit;
  mergedThemeSettings.avatarBorderWidth =
    mergedThemeSettings.avatarBorderWidth ?? initialProfileState.themeSettings.avatarBorderWidth;
  mergedThemeSettings.avatarBorderColor =
    mergedThemeSettings.avatarBorderColor || initialProfileState.themeSettings.avatarBorderColor;
  mergedThemeSettings.avatarShadow =
    typeof mergedThemeSettings.avatarShadow === 'boolean'
      ? mergedThemeSettings.avatarShadow
      : initialProfileState.themeSettings.avatarShadow;
  mergedThemeSettings.avatarAlign =
    mergedThemeSettings.avatarAlign || initialProfileState.themeSettings.avatarAlign;

  mergedThemeSettings.avatarBgMode =
    mergedThemeSettings.avatarBgMode || initialProfileState.themeSettings.avatarBgMode;
  mergedThemeSettings.avatarBgColor =
    mergedThemeSettings.avatarBgColor || initialProfileState.themeSettings.avatarBgColor;
  mergedThemeSettings.avatarBgOpacity =
    typeof mergedThemeSettings.avatarBgOpacity === 'number'
      ? mergedThemeSettings.avatarBgOpacity
      : initialProfileState.themeSettings.avatarBgOpacity;
  mergedThemeSettings.avatarBgApplyMode =
    mergedThemeSettings.avatarBgApplyMode || initialProfileState.themeSettings.avatarBgApplyMode;

  // ensure customLinks is array
  const mergedCustomLinks = Array.isArray(data.customLinks)
    ? data.customLinks
    : [];

  const migrated = {
    ...initialProfileState,
    ...data,
    contactLinks: mergedContactLinks,
    themeSettings: mergedThemeSettings,
    customLinks: mergedCustomLinks,
  };

  // safety defaults
  migrated.contactLinksDisplay =
    migrated.contactLinksDisplay || 'icons';
  migrated.themeSettings.gradientAngle =
    migrated.themeSettings.gradientAngle ?? 180;

  return migrated;
};

/* ===================== WhatsApp helpers ===================== */
const cleanPhoneNumber = (number) => {
  if (!number) return '';
  let cleaned = number.replace(/[^0-9+]/g, '');
  if (cleaned.lastIndexOf('+') > 0) {
    cleaned = cleaned.replace(/\+/g, '');
  }
  if (cleaned.startsWith('++')) {
    cleaned = '+' + cleaned.substring(2);
  }
  return cleaned;
};

const extractWaNumber = (url) => {
  if (!url || !url.includes('wa.me/')) return '';
  return `+${url
    .split('wa.me/')[1]
    .replace(/[^0-9]/g, '')}`;
};

/* ===================== Image crop helpers ===================== */
// read File/Blob to dataURL
const fileToDataURL = (file) =>
  new Promise((resolve, reject) => {
    const fr = new FileReader();
    fr.onload = () => resolve(fr.result);
    fr.onerror = reject;
    fr.readAsDataURL(file);
  });

const createImage = (url) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = url;
  });

const getCroppedBlob = async (imageSrc, cropPixels, outputType = 'image/jpeg', quality = 0.9) => {
  const image = await createImage(imageSrc);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  const pixelRatio = window.devicePixelRatio || 1;
  canvas.width = Math.max(1, Math.floor(cropPixels.width * pixelRatio));
  canvas.height = Math.max(1, Math.floor(cropPixels.height * pixelRatio));

  ctx.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
  ctx.imageSmoothingQuality = 'high';

  ctx.drawImage(
    image,
    cropPixels.x,
    cropPixels.y,
    cropPixels.width,
    cropPixels.height,
    0,
    0,
    cropPixels.width,
    cropPixels.height
  );

  return new Promise((resolve) => {
    canvas.toBlob(
      (blob) => resolve(blob),
      outputType,
      quality
    );
  });
};

/* ---- util: hex + opacity -> rgba ---- */
const hexToRgba = (hex, alpha = 1) => {
  try {
    const h = String(hex || '#000').replace('#', '');
    const full = h.length === 3 ? h.split('').map((c) => c + c).join('') : h;
    const n = parseInt(full, 16);
    const r = (n >> 16) & 255;
    const g = (n >> 8) & 255;
    const b = n & 255;
    return `rgba(${r}, ${g}, ${b}, ${Math.min(Math.max(alpha, 0), 1)})`;
  } catch {
    return `rgba(0,0,0,${Math.min(Math.max(alpha, 0), 1)})`;
  }
};

/* ---- util: detect alpha (transparency) in cropped region ---- */
const imageRegionHasAlpha = async (imageSrc, region) => {
  const img = await createImage(imageSrc);
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.floor(region.width));
  canvas.height = Math.max(1, Math.floor(region.height));
  const ctx = canvas.getContext('2d');
  ctx.drawImage(
    img,
    region.x, region.y, region.width, region.height,
    0, 0, region.width, region.height
  );
  const { data } = ctx.getImageData(0, 0, canvas.width, canvas.height);
  for (let i = 3; i < data.length; i += 4) {
    if (data[i] < 255) return true; // at least one transparent pixel
  }
  return false;
};

/* ===================== Component ===================== */
function UserDashboard() {
  // auth state
  const [user, loadingAuth, errorAuth] = useAuthState(auth);
  const navigate = useNavigate();

  // profile state
  const [profileData, setProfileData] = useState(initialProfileState);

  // upload image state
  const [imageFile, setImageFile] = useState(null); // final (cropped) file to upload
  const [isConvertingImage, setIsConvertingImage] = useState(false); // HEIC converting

  // cropper modal state
  const [cropperOpen, setCropperOpen] = useState(false);
  const [rawImageSrc, setRawImageSrc] = useState(null); // dataURL used by Cropper
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [aspect, setAspect] = useState(1); // 1:1 للصور الشخصية
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const objectUrlRef = useRef(null); // لإدارة revokeObjectURL عند اللزوم

  // transparency flag
  const [photoHasAlpha, setPhotoHasAlpha] = useState(false);

  // ui state
  const [loadingData, setLoadingData] = useState(true);
  const [error, setError] = useState(null);
  const [showQRModal, setShowQRModal] = useState(false);

  // edit / view toggle
  const [isEditing, setIsEditing] = useState(false);

  /* ---------------- Fetch profile data on mount / auth change ---------------- */
  useEffect(() => {
    if (loadingAuth) return;

    if (!user) {
      navigate('/login');
      return;
    }

    let isMounted = true;

    const fetchProfileData = async () => {
      if (isMounted) setLoadingData(true);
      setError(null);

      try {
        const userDocRef = doc(db, 'users', user.uid);
        const snap = await getDoc(userDocRef);

        if (!isMounted) return;

        if (snap.exists()) {
          const dataFromDB = snap.data();

          // redirect admins to admin panel instead of dashboard
          if (dataFromDB.isAdmin) {
            navigate('/admin/users');
            return;
          }

          const migrated = migrateData(dataFromDB);
          setProfileData(migrated);
          setIsEditing(false);
          // restore alpha flag if saved before
          if (typeof dataFromDB.photoHasAlpha === 'boolean') {
            setPhotoHasAlpha(dataFromDB.photoHasAlpha);
          }
        } else {
          setError('User profile data not found in database.');
          setProfileData(initialProfileState);
        }
      } catch (err) {
        console.error('Error fetching profile data:', err);
        if (isMounted) {
          setError('Failed to load profile data.');
          setProfileData(migrateData({}));
        }
      } finally {
        if (isMounted) setLoadingData(false);
      }
    };

    fetchProfileData();
    return () => { isMounted = false; };
  }, [user, loadingAuth, navigate]);

  /* ---------------- Form change handler ---------------- */
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    // contactLinks.phone / contactLinks.email / ...
    if (name.startsWith('contactLinks.')) {
      const linkName = name.split('.')[1];

      if (linkName === 'whatsapp') {
        const cleanedNumber = cleanPhoneNumber(value);
        const fullUrl = cleanedNumber
          ? `https://wa.me/${cleanedNumber.replace('+', '')}`
          : '';
        setProfileData((prev) => ({
          ...prev,
          contactLinks: {
            ...prev.contactLinks,
            whatsapp: fullUrl,
          },
        }));
      } else {
        setProfileData((prev) => ({
          ...prev,
          contactLinks: {
            ...prev.contactLinks,
            [linkName]: value,
          },
        }));
      }
      return;
    }

    // themeSettings.*
    if (name.startsWith('themeSettings.')) {
      const settingName = name.split('.')[1];

      // special float for avatarBgOpacity
      if (settingName === 'avatarBgOpacity') {
        const v = Math.max(0, Math.min(1, parseFloat(value)));
        setProfileData((prev) => ({
          ...prev,
          themeSettings: {
            ...prev.themeSettings,
            [settingName]: isNaN(v) ? prev.themeSettings.avatarBgOpacity : v,
          },
        }));
        return;
      }

      const intKeys = new Set(['gradientAngle', 'avatarSize', 'avatarBorderWidth']);
      const settingValue =
        type === 'checkbox'
          ? checked
          : intKeys.has(settingName)
          ? parseInt(value, 10)
          : value;

      setProfileData((prev) => ({
        ...prev,
        themeSettings: {
          ...prev.themeSettings,
          [settingName]: settingValue,
        },
      }));
      return;
    }

    // top-level fields
    setProfileData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  /* ---------------- Custom Links editing helpers ---------------- */
  const handleCustomLinkChange = (index, field, value) => {
    const newLinks = profileData.customLinks.map((link, i) =>
      i === index ? { ...link, [field]: value } : link
    );
    setProfileData((prev) => ({
      ...prev,
      customLinks: newLinks,
    }));
  };

  const deleteCustomLink = (index) => {
    const newLinks = profileData.customLinks.filter((_, i) => i !== index);
    setProfileData((prev) => ({ ...prev, customLinks: newLinks }));
  };

  const addNewCustomLink = () => {
    const newLink = {
      id: `link-${Date.now()}`,
      type: 'link',
      title: 'New Custom Link',
      url: 'https://',
      clicks: 0,
    };
    setProfileData((prev) => ({
      ...prev,
      customLinks: [...(prev.customLinks || []), newLink],
    }));
  };

  const handleOnDragEnd = (result) => {
    if (!result.destination) return;
    const items = Array.from(profileData.customLinks || []);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);
    setProfileData((prev) => ({ ...prev, customLinks: items }));
  };

  /* ---------------- File change (with HEIC conversion) and CROP flow ---------------- */
  const resetCropper = () => {
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setCroppedAreaPixels(null);
  };

  const revokeObjectUrlIfAny = () => {
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = null;
    }
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setError(null);
    setIsConvertingImage(true);
    revokeObjectUrlIfAny();

    try {
      let workingFile = file;

      // HEIC → JPEG
      const lower = (file.type || '').toLowerCase();
      const lowerName = (file.name || '').toLowerCase();
      const isHeic =
        lower.includes('heic') ||
        lower.includes('heif') ||
        lowerName.endsWith('.heic') ||
        lowerName.endsWith('.heif');

      if (isHeic) {
        const convertedBlob = await heic2any({
          blob: file,
          toType: 'image/jpeg',
          quality: 0.94,
        });
        workingFile = new File([convertedBlob], lowerName.replace(/\.(heic|heif)$/i, '.jpg'), {
          type: 'image/jpeg',
          lastModified: Date.now(),
        });
      }

      // read to dataURL for cropper
      const dataURL = await fileToDataURL(workingFile);
      setRawImageSrc(dataURL);

      // open cropper (1:1)
      setAspect(1);
      resetCropper();
      setCropperOpen(true);
    } catch (conversionError) {
      console.error('HEIC/Read Error:', conversionError);
      setError('Failed to read/convert image. Please try JPEG or PNG.');
    } finally {
      setIsConvertingImage(false);
    }
  };

  const onCropComplete = useCallback((_, croppedPixels) => {
    setCroppedAreaPixels(croppedPixels);
  }, []);

  const confirmCrop = async () => {
    try {
      if (!rawImageSrc || !croppedAreaPixels) return;

      // detect transparency in the cropped region
      const hasAlpha = await imageRegionHasAlpha(rawImageSrc, croppedAreaPixels);
      const mime = hasAlpha ? 'image/png' : 'image/jpeg';
      const quality = hasAlpha ? 1 : 0.92;

      // cropped blob with proper mime
      const blob = await getCroppedBlob(rawImageSrc, croppedAreaPixels, mime, quality);
      const ext = hasAlpha ? 'png' : 'jpg';
      const croppedFile = new File([blob], `avatar_${Date.now()}.${ext}`, {
        type: mime,
        lastModified: Date.now(),
      });

      setImageFile(croppedFile);
      setPhotoHasAlpha(hasAlpha);

      // local preview until upload
      const previewUrl = URL.createObjectURL(croppedFile);
      objectUrlRef.current = previewUrl;
      setProfileData((prev) => ({ ...prev, photoURL: previewUrl }));

      setCropperOpen(false);
    } catch (err) {
      console.error('Crop Error:', err);
      setError('Failed to crop image.');
    }
  };

  const cancelCrop = () => {
    setCropperOpen(false);
    setRawImageSrc(null);
    resetCropper();
  };

  /* ---------------- Save handler (deep merge + uniqueness + upload) ---------------- */
  const handleSave = async () => {
    if (!user || isConvertingImage) return;
    setLoadingData(true);
    setError(null);

    try {
      const userRef = doc(db, 'users', user.uid);

      // 1) get previous for deep merge
      const prevSnap = await getDoc(userRef);
      const prevData = prevSnap.exists() ? prevSnap.data() : {};

      // 2) username uniqueness check
      const usernameQuery = query(
        collection(db, 'users'),
        where('username', '==', profileData.username)
      );
      const querySnapshot = await getDocs(usernameQuery);

      let usernameTaken = false;
      querySnapshot.forEach((d) => {
        if (d.id !== user.uid) {
          usernameTaken = true;
        }
      });
      if (usernameTaken) throw new Error('This username is already taken.');

      // 3) upload image if changed (only cropped)
      let newPhotoURL = profileData.photoURL;
      if (imageFile) {
        const storageRef = ref(storage, `profile_images/${user.uid}/${imageFile.name}`);
        const uploadTask = await uploadBytes(storageRef, imageFile);
        newPhotoURL = await getDownloadURL(uploadTask.ref);
        revokeObjectUrlIfAny();
      }

      // 4) deep merge for nested objects
      const mergedContactLinks = {
        ...initialProfileState.contactLinks,
        ...(prevData.contactLinks || {}),
        ...(profileData.contactLinks || {}),
      };

      const mergedThemeSettings = {
        ...initialProfileState.themeSettings,
        ...(prevData.themeSettings || {}),
        ...(profileData.themeSettings || {}),
      };

      const mergedCustomLinks = Array.isArray(profileData.customLinks)
        ? profileData.customLinks
        : prevData.customLinks || [];

      // 5) build final doc
      const dataToSave = {
        ...prevData,
        ...profileData,
        uid: user.uid,
        photoURL: newPhotoURL,
        contactLinks: mergedContactLinks,
        themeSettings: mergedThemeSettings,
        customLinks: mergedCustomLinks,
        photoHasAlpha: typeof photoHasAlpha === 'boolean'
          ? photoHasAlpha
          : (prevData.photoHasAlpha || false),
      };

      // 6) write
      await setDoc(userRef, dataToSave);

      // 7) normalize
      const stableAfterSave = migrateData(dataToSave);

      // 8) update local state
      setProfileData(stableAfterSave);
      setImageFile(null);
      setIsEditing(false);

      alert('Profile Saved Successfully!');
    } catch (err) {
      console.error('Save Error:', err);
      setError(err.message || 'Failed to save profile.');
    }

    setLoadingData(false);
  };

  /* ---------------- Logout ---------------- */
  const handleLogout = () => {
    signOut(auth);
  };

  /* ---------------- QR download ---------------- */
  const downloadQR = () => {
    const canvas = document.getElementById('qr-code-canvas');
    if (!canvas) return;

    const pngUrl = canvas.toDataURL('image/png').replace('image/png', 'image/octet-stream');
    const a = document.createElement('a');
    a.href = pngUrl;
    a.download = `${profileData.username}-qr.png`;

    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  /* ---------------- helper for read-only rows ---------------- */
  const renderContactRow = (label, valueShown) => {
    if (valueShown === null || valueShown === undefined) return null;
    const text = String(valueShown);
    if (text.trim() === '') return null;

    return (
      <div className="profile-read-row">
        <strong>{label}:</strong>
        <span>{text}</span>
      </div>
    );
  };

  // ----- avatar computed styles for local preview inside dashboard -----
  const ts = profileData.themeSettings || {};
  const avatarSize = ts.avatarSize ?? 110;
  const avatarShape = ts.avatarShape || 'circle';
  const avatarObjectFit = ts.avatarObjectFit || 'cover';
  const avatarBorderWidth = ts.avatarBorderWidth ?? 4;
  const avatarBorderColor = ts.avatarBorderColor || 'rgba(255,255,255,0.9)';
  const avatarShadowOn = typeof ts.avatarShadow === 'boolean' ? ts.avatarShadow : true;
  const avatarAlign = ts.avatarAlign || 'center';

  const avatarBgMode = ts.avatarBgMode || 'transparent';
  const avatarBgColor = ts.avatarBgColor || '#ffffff';
  const avatarBgOpacity = typeof ts.avatarBgOpacity === 'number' ? ts.avatarBgOpacity : 1;
  const avatarBgApplyMode = ts.avatarBgApplyMode || 'onlyTransparent';

  const borderRadius =
    avatarShape === 'circle' ? '999px' : avatarShape === 'rounded' ? '22px' : '0px';

  // Apply bg only if mode=color AND (apply always OR image has alpha)
  const shouldApplyAvatarBg =
    avatarBgMode === 'color' &&
    (avatarBgApplyMode === 'always' || photoHasAlpha);

  const avatarContainerStyle = {
    width: avatarSize,
    height: avatarSize,
    borderRadius,
    margin: avatarAlign === 'center' ? '0 auto 15px auto' : '0 0 15px 0',
    display: 'grid',
    placeItems: 'center',
    overflow: 'hidden',
    background: shouldApplyAvatarBg ? hexToRgba(avatarBgColor, avatarBgOpacity) : 'transparent',
    border: `${avatarBorderWidth}px solid ${avatarBorderColor}`,
    boxShadow: avatarShadowOn
      ? '0 20px 40px rgba(0,0,0,0.35), 0 2px 4px rgba(0,0,0,0.4)'
      : 'none',
  };

  const avatarImgStyle = {
    width: '100%',
    height: '100%',
    objectFit: avatarObjectFit,
    display: 'block',
  };

  /* ---------------- Loading / error states ---------------- */
  if (loadingAuth) {
    return <div className="center-fullscreen-msg">Authenticating...</div>;
  }

  if (loadingData && !error) {
    return <div className="center-fullscreen-msg">Loading Profile Data...</div>;
  }

  if (errorAuth) {
    return (
      <div style={{ padding: 20, color: 'red' }}>
        Error loading user authentication: {errorAuth.message}
      </div>
    );
  }

  if (error && !loadingData) {
    return (
      <div style={{ padding: 20, color: 'red' }}>
        Error: {error}{' '}
        <button onClick={() => window.location.reload()}>Retry</button>
      </div>
    );
  }

  if (!profileData.username && !loadingData) {
    return (
      <div style={{ padding: 20, color: 'orange' }}>
        Profile data seems incomplete. Trying to load...{' '}
        <button onClick={() => window.location.reload()}>Retry</button>
      </div>
    );
  }

  /* ---------------- UI Render ---------------- */
  return (
    <div className="user-dashboard-layout">
      {/* HEADER */}
      <header className="user-header">
        <h1>My Profile</h1>

        <div className="user-header-actions">
          <a
            href={`/${profileData.username}`}
            target="_blank"
            rel="noopener noreferrer"
            className="action-button preview-button"
          >
            View Public Page
          </a>

          <button
            onClick={() => setShowQRModal(true)}
            className="action-button"
            style={{ backgroundColor: '#6c757d', color: 'white' }}
          >
            Get QR Code
          </button>

          {isEditing ? (
            <button
              onClick={handleSave}
              className="action-button save-button"
              disabled={loadingData || isConvertingImage}
            >
              {loadingData ? 'Saving...' : isConvertingImage ? 'Converting...' : 'Save Changes'}
            </button>
          ) : (
            <button
              onClick={() => setIsEditing(true)}
              className="action-button save-button"
            >
              Edit Profile
            </button>
          )}

          <button
            onClick={handleLogout}
            className="action-button logout-button"
          >
            Logout
          </button>
        </div>
      </header>

      {/* MAIN AREA: left (form or readonly), right (preview) */}
      <main className="user-main-content">
        {/* LEFT SIDE */}
        <div className="user-form-container">
          {isEditing ? (
            /* ====================== EDIT MODE ====================== */
            <form
              className="user-form"
              onSubmit={(e) => {
                e.preventDefault();
                handleSave();
              }}
            >
              {isConvertingImage && (
                <p style={{ textAlign: 'center', color: 'blue', width: '100%' }}>
                  Converting HEIC image...
                </p>
              )}

              {error && (
                <p className="error-message" style={{ textAlign: 'center', color: 'red', width: '100%' }}>
                  {error}
                </p>
              )}

              {/* --- Main Details --- */}
              <div className="form-section">
                <h3>Main Details</h3>

                {profileData.photoURL && (
                  <div style={avatarContainerStyle}>
                    <img
                      src={profileData.photoURL}
                      alt="Profile"
                      style={avatarImgStyle}
                    />
                  </div>
                )}

                <div>
                  <label>Profile Image/Logo:</label>
                  <input
                    type="file"
                    accept="image/*,.heic,.heif"
                    onChange={handleFileChange}
                  />
                  <small style={{ display: 'block', color: '#777', marginTop: 6 }}>
                    اختر صورة، ستفتح لك واجهة قصّ لتحديد الجزء الظاهر (تحريك/تكبير).
                  </small>
                </div>

                <div>
                  <label>Display Name:</label>
                  <input
                    type="text"
                    name="displayName"
                    value={profileData.displayName || ''}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div>
                  <label>Username (Your unique link):</label>
                  <input
                    type="text"
                    name="username"
                    value={profileData.username || ''}
                    onChange={handleChange}
                    required
                    pattern="[a-zA-Z0-9_-]+"
                    title="Username can only contain letters, numbers, underscores, and hyphens."
                  />
                </div>
              </div>

              {/* --- Appearance & Theme --- */}
              <div className="form-section">
                <h3>Appearance & Theme</h3>

                <div>
                  <label>Profile Type:</label>
                  <select
                    name="profileType"
                    value={profileData.profileType}
                    onChange={handleChange}
                  >
                    <option value="personal">Personal</option>
                    <option value="company">Company</option>
                  </select>
                </div>

                <div>
                  <label>Profile Theme:</label>
                  <select
                    name="theme"
                    value={profileData.theme}
                    onChange={handleChange}
                  >
                    <option value="light">Light</option>
                    <option value="dark">Dark</option>
                    <option value="forest">Forest</option>
                    <option value="neon">Neon</option>
                    <option value="sunset">Sunset</option>
                    <option value="custom">Custom Theme</option>
                  </select>
                </div>

                <div>
                  <label>Contact Links Display Mode:</label>
                  <select
                    name="contactLinksDisplay"
                    value={profileData.contactLinksDisplay || 'icons'}
                    onChange={handleChange}
                  >
                    <option value="icons">Icons Row (minimal)</option>
                    <option value="buttons">Big Buttons (Cards)</option>
                    <option value="chips">Chips / Pills</option>
                    <option value="list">List with Values</option>
                    <option value="grid">Grid Cards (2 columns)</option>
                    <option value="minimal_text">Minimal Text Links</option>
                    <option value="qr_block">Main Links + QR Code</option>
                  </select>
                </div>
              </div>

              {/* --- NEW: Profile Image Display (Avatar) --- */}
              <div className="form-section">
                <h3>Profile Image Display</h3>

                <div>
                  <label>Image Size (px): {avatarSize}</label>
                  <input
                    type="range"
                    min="60"
                    max="220"
                    step="2"
                    name="themeSettings.avatarSize"
                    value={avatarSize}
                    onChange={handleChange}
                    style={{ width: '100%' }}
                  />
                </div>

                <div>
                  <label>Shape:</label>
                  <select
                    name="themeSettings.avatarShape"
                    value={avatarShape}
                    onChange={handleChange}
                  >
                    <option value="circle">Circle</option>
                    <option value="rounded">Rounded</option>
                    <option value="square">Square</option>
                  </select>
                </div>

                <div>
                  <label>Object Fit:</label>
                  <select
                    name="themeSettings.avatarObjectFit"
                    value={avatarObjectFit}
                    onChange={handleChange}
                  >
                    <option value="cover">Cover (crop edges)</option>
                    <option value="contain">Contain (fit inside)</option>
                  </select>
                </div>

                <div>
                  <label>Border Width (px): {avatarBorderWidth}</label>
                  <input
                    type="range"
                    min="0"
                    max="10"
                    step="1"
                    name="themeSettings.avatarBorderWidth"
                    value={avatarBorderWidth}
                    onChange={handleChange}
                    style={{ width: '100%' }}
                  />
                </div>

                <div>
                  <label>Border Color:</label>
                  <input
                    type="color"
                    name="themeSettings.avatarBorderColor"
                    value={
                      String(avatarBorderColor).startsWith('#')
                        ? avatarBorderColor
                        : '#ffffff'
                    }
                    onChange={handleChange}
                    style={{ height: 40, width: '100%', padding: '5px', boxSizing: 'border-box' }}
                  />
                </div>

                <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <input
                      type="checkbox"
                      name="themeSettings.avatarShadow"
                      checked={!!avatarShadowOn}
                      onChange={handleChange}
                    />
                    Shadow
                  </label>

                  <div style={{ flex: 1 }}>
                    <label>Align:</label>
                    <select
                      name="themeSettings.avatarAlign"
                      value={avatarAlign}
                      onChange={handleChange}
                    >
                      <option value="center">Center</option>
                      <option value="left">Left</option>
                    </select>
                  </div>
                </div>

                {/* ---- NEW: Avatar Background ---- */}
                <hr style={{ margin: '16px 0', border: 0, borderTop: '1px solid #eee' }} />
                <h4 style={{ marginTop: 0 }}>Avatar Background</h4>

                <div>
                  <label>Background Mode:</label>
                  <select
                    name="themeSettings.avatarBgMode"
                    value={avatarBgMode}
                    onChange={handleChange}
                  >
                    <option value="transparent">Transparent</option>
                    <option value="color">Solid Color</option>
                  </select>
                </div>

                {avatarBgMode === 'color' && (
                  <>
                    <div>
                      <label>Background Color:</label>
                      <input
                        type="color"
                        name="themeSettings.avatarBgColor"
                        value={
                          String(avatarBgColor).startsWith('#')
                            ? avatarBgColor
                            : '#ffffff'
                        }
                        onChange={handleChange}
                        style={{ height: 40, width: '100%', padding: '5px', boxSizing: 'border-box' }}
                      />
                    </div>

                    <div>
                      <label>Opacity: {Math.round((avatarBgOpacity ?? 1) * 100)}%</label>
                      <input
                        type="range"
                        min="0"
                        max="100"
                        step="1"
                        value={Math.round((avatarBgOpacity ?? 1) * 100)}
                        onChange={(e) => {
                          const v = Math.max(0, Math.min(100, parseInt(e.target.value, 10)));
                          handleChange({
                            target: {
                              name: 'themeSettings.avatarBgOpacity',
                              value: String(v / 100),
                              type: 'text',
                            },
                          });
                        }}
                        style={{ width: '100%' }}
                      />
                    </div>

                    <div>
                      <label>Apply Background:</label>
                      <select
                        name="themeSettings.avatarBgApplyMode"
                        value={avatarBgApplyMode}
                        onChange={handleChange}
                      >
                        <option value="onlyTransparent">Only if image is transparent (PNG)</option>
                        <option value="always">Always apply</option>
                      </select>
                    </div>
                  </>
                )}
              </div>

              {/* --- Custom Theme Editor --- */}
              {profileData.theme === 'custom' && (
                <div className="form-section">
                  <h3>Custom Theme Editor</h3>

                  <div>
                    <label>Background Type:</label>
                    <select
                      name="themeSettings.backgroundType"
                      value={profileData.themeSettings?.backgroundType || 'color'}
                      onChange={handleChange}
                    >
                      <option value="color">Solid Color</option>
                      <option value="gradient">Gradient</option>
                    </select>
                  </div>

                  {profileData.themeSettings?.backgroundType === 'color' && (
                    <div>
                      <label>Background Color:</label>
                      <input
                        type="color"
                        name="themeSettings.backgroundColor"
                        value={profileData.themeSettings?.backgroundColor || '#ffffff'}
                        onChange={handleChange}
                        style={{ height: 40, padding: '5px', boxSizing: 'border-box', width: '100%' }}
                      />
                    </div>
                  )}

                  {profileData.themeSettings?.backgroundType === 'gradient' && (
                    <>
                      <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                        <div style={{ flex: 1 }}>
                          <label>Gradient Start:</label>
                          <input
                            type="color"
                            name="themeSettings.gradientStart"
                            value={profileData.themeSettings?.gradientStart || '#ffffff'}
                            onChange={handleChange}
                            style={{ height: 40, padding: '5px', boxSizing: 'border-box', width: '100%' }}
                          />
                        </div>

                        <div style={{ flex: 1 }}>
                          <label>Gradient End:</label>
                          <input
                            type="color"
                            name="themeSettings.gradientEnd"
                            value={profileData.themeSettings?.gradientEnd || '#000000'}
                            onChange={handleChange}
                            style={{ height: 40, padding: '5px', boxSizing: 'border-box', width: '100%' }}
                          />
                        </div>
                      </div>

                      <div>
                        <label>
                          Gradient Angle ({profileData.themeSettings?.gradientAngle ?? 180}°):
                        </label>
                        <input
                          type="range"
                          name="themeSettings.gradientAngle"
                          min="0"
                          max="360"
                          value={profileData.themeSettings?.gradientAngle ?? 180}
                          onChange={handleChange}
                          style={{ width: '100%' }}
                        />
                      </div>
                    </>
                  )}

                  <hr style={{ margin: '20px 0', border: 0, borderTop: '1px solid #f0f0f0' }} />

                  <div>
                    <label>Button Style:</label>
                    <select
                      name="themeSettings.buttonStyle"
                      value={profileData.themeSettings?.buttonStyle || 'rounded'}
                      onChange={handleChange}
                    >
                      <option value="rounded">Rounded</option>
                      <option value="square">Square</option>
                      <option value="outline">Outline</option>
                    </select>
                  </div>

                  <div style={{ display: 'flex', gap: 10 }}>
                    <div style={{ flex: 1 }}>
                      <label>Button Color:</label>
                      <input
                        type="color"
                        name="themeSettings.buttonColor"
                        value={profileData.themeSettings?.buttonColor || '#007bff'}
                        onChange={handleChange}
                        style={{ height: 40, padding: '5px', boxSizing: 'border-box', width: '100%' }}
                      />
                    </div>

                    <div style={{ flex: 1 }}>
                      <label>Button Text Color:</label>
                      <input
                        type="color"
                        name="themeSettings.buttonFontColor"
                        value={profileData.themeSettings?.buttonFontColor || '#000000'}
                        onChange={handleChange}
                        style={{ height: 40, padding: '5px', boxSizing: 'border-box', width: '100%' }}
                      />
                    </div>
                  </div>

                  <div>
                    <label>Font Family:</label>
                    <select
                      name="themeSettings.fontFamily"
                      value={profileData.themeSettings?.fontFamily || 'Arial, sans-serif'}
                      onChange={handleChange}
                    >
                      <option value="Arial, sans-serif">Arial</option>
                      <option value="'Roboto', sans-serif">Roboto</option>
                      <option value="'Montserrat', sans-serif">Montserrat</option>
                      <option value="'Playfair Display', serif">Playfair Display</option>
                    </select>
                  </div>
                </div>
              )}

              {/* --- Contact Links --- */}
              <div className="form-section">
                <h3>Contact Links (Social Icons)</h3>
                <p style={{ fontSize: '0.9em', color: '#666', marginTop: '-10px' }}>
                  These links appear based on the display mode selected.
                </p>

                <div>
                  <label>Phone:</label>
                  <input
                    type="tel"
                    name="contactLinks.phone"
                    value={profileData.contactLinks?.phone || ''}
                    onChange={handleChange}
                    placeholder="tel:+970..."
                  />
                </div>

                <div>
                  <label>Email:</label>
                  <input
                    type="email"
                    name="contactLinks.email"
                    value={profileData.contactLinks?.email || ''}
                    onChange={handleChange}
                    placeholder="mailto:info@..."
                  />
                </div>

                <div>
                  <label>WhatsApp Number (with country code):</label>
                  <input
                    type="tel"
                    name="contactLinks.whatsapp"
                    value={extractWaNumber(profileData.contactLinks?.whatsapp || '') || ''}
                    onChange={handleChange}
                    placeholder="+970..."
                  />
                </div>

                <div>
                  <label>Instagram:</label>
                  <input
                    type="text"
                    name="contactLinks.instagram"
                    value={profileData.contactLinks?.instagram || ''}
                    onChange={handleChange}
                    placeholder="https://instagram.com/username"
                  />
                </div>

                <div>
                  <label>Facebook:</label>
                  <input
                    type="text"
                    name="contactLinks.facebook"
                    value={profileData.contactLinks?.facebook || ''}
                    onChange={handleChange}
                    placeholder="https://facebook.com/username"
                  />
                </div>

                <div>
                  <label>TikTok:</label>
                  <input
                    type="text"
                    name="contactLinks.tiktok"
                    value={profileData.contactLinks?.tiktok || ''}
                    onChange={handleChange}
                    placeholder="https://tiktok.com/@username"
                  />
                </div>

                <div>
                  <label>LinkedIn:</label>
                  <input
                    type="text"
                    name="contactLinks.linkedin"
                    value={profileData.contactLinks?.linkedin || ''}
                    onChange={handleChange}
                    placeholder="https://linkedin.com/in/username"
                  />
                </div>

                <div>
                  <label>Snapchat:</label>
                  <input
                    type="text"
                    name="contactLinks.snapchat"
                    value={profileData.contactLinks?.snapchat || ''}
                    onChange={handleChange}
                    placeholder="https://snapchat.com/add/username"
                  />
                </div>

                <div>
                  <label>X (Twitter):</label>
                  <input
                    type="text"
                    name="contactLinks.x"
                    value={profileData.contactLinks?.x || ''}
                    onChange={handleChange}
                    placeholder="https://x.com/username"
                  />
                </div>
              </div>

              {/* --- Custom Links (Drag & Drop) --- */}
              <div className="form-section">
                <h3>Custom Links (Buttons)</h3>
                <p style={{ fontSize: '0.9em', color: '#666', marginTop: '-10px' }}>
                  Add custom links. Drag to re-order.
                </p>

                <DragDropContext onDragEnd={handleOnDragEnd}>
                  <Droppable droppableId="customLinks">
                    {(provided) => (
                      <div {...provided.droppableProps} ref={provided.innerRef}>
                        {(profileData.customLinks || []).map((link, index) => (
                          <Draggable key={link.id} draggableId={link.id} index={index}>
                            {(provided) => (
                              <div
                                className="link-editor-card"
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                              >
                                <span className="drag-handle">::</span>

                                <div className="link-editor-content">
                                  <div>
                                    <label>Title</label>
                                    <input
                                      type="text"
                                      value={link.title || ''}
                                      onChange={(e) =>
                                        handleCustomLinkChange(index, 'title', e.target.value)
                                      }
                                    />
                                  </div>

                                  <div>
                                    <label>URL</label>
                                    <input
                                      type="text"
                                      value={link.url || ''}
                                      onChange={(e) =>
                                        handleCustomLinkChange(index, 'url', e.target.value)
                                      }
                                    />
                                  </div>
                                </div>

                                <button
                                  type="button"
                                  onClick={() => deleteCustomLink(index)}
                                  className="delete-link-btn"
                                >
                                  X
                                </button>
                              </div>
                            )}
                          </Draggable>
                        ))}

                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>
                </DragDropContext>

                <button
                  type="button"
                  onClick={addNewCustomLink}
                  className="action-button save-button"
                  style={{ width: '100%', marginTop: '20px' }}
                >
                  + Add Custom Link
                </button>
              </div>
            </form>
          ) : (
            /* ====================== READ ONLY MODE ====================== */
            <div className="user-readonly-card">
              {error && (
                <p className="error-message" style={{ textAlign: 'center', color: 'red', width: '100%' }}>
                  {error}
                </p>
              )}

              {/* --- Main Details (read-only) --- */}
              <div className="form-section">
                <h3>Main Details</h3>

                {profileData.photoURL && (
                  <div style={avatarContainerStyle}>
                    <img
                      src={profileData.photoURL}
                      alt="Profile"
                      style={avatarImgStyle}
                    />
                  </div>
                )}

                {renderContactRow('Display Name', profileData.displayName)}
                {renderContactRow('Username', profileData.username)}
                {renderContactRow('Profile Type', profileData.profileType)}
                {renderContactRow('Theme', profileData.theme)}
                {renderContactRow('Links Display', profileData.contactLinksDisplay)}

                {/* summary of avatar settings */}
                {renderContactRow('Avatar Shape', avatarShape)}
                {renderContactRow('Avatar Size', avatarSize)}
                {renderContactRow('Object Fit', avatarObjectFit)}
                {renderContactRow('Border Width', avatarBorderWidth)}
                {renderContactRow('Border Color', avatarBorderColor)}
                {renderContactRow('Shadow', avatarShadowOn ? 'On' : 'Off')}
                {renderContactRow('Align', avatarAlign)}
                {renderContactRow('Avatar Bg Mode', avatarBgMode)}
                {renderContactRow('Avatar Bg Apply', avatarBgApplyMode)}
                {renderContactRow('Avatar Bg Color', avatarBgMode === 'color' ? avatarBgColor : '—')}
                {renderContactRow('Avatar Bg Opacity', avatarBgMode === 'color' ? `${Math.round(avatarBgOpacity * 100)}%` : '—')}
                {renderContactRow('Photo Has Alpha', photoHasAlpha ? 'Yes' : 'No')}
              </div>

              {/* --- Contact & Social (read-only) --- */}
              <div className="form-section">
                <h3>Contact & Social</h3>

                {renderContactRow('Phone', profileData.contactLinks?.phone)}
                {renderContactRow('Email', profileData.contactLinks?.email)}
                {renderContactRow('WhatsApp', extractWaNumber(profileData.contactLinks?.whatsapp || ''))}
                {renderContactRow('Instagram', profileData.contactLinks?.instagram)}
                {renderContactRow('Facebook', profileData.contactLinks?.facebook)}
                {renderContactRow('TikTok', profileData.contactLinks?.tiktok)}
                {renderContactRow('LinkedIn', profileData.contactLinks?.linkedin)}
                {renderContactRow('Snapchat', profileData.contactLinks?.snapchat)}
                {renderContactRow('X', profileData.contactLinks?.x)}
              </div>

              {/* --- Custom Links (read-only) --- */}
              <div className="form-section">
                <h3>Custom Links</h3>

                {(profileData.customLinks || []).length === 0 && (
                  <div className="profile-read-row">
                    <em>No custom links added.</em>
                  </div>
                )}

                {(profileData.customLinks || []).map((link) => (
                  <div
                    key={link.id}
                    className="profile-read-row"
                    style={{ flexDirection: 'column', alignItems: 'flex-start' }}
                  >
                    <strong>{link.title}</strong>
                    <span
                      style={{
                        fontSize: '0.9em',
                        color: '#555',
                        wordBreak: 'break-all',
                      }}
                    >
                      {link.url}
                    </span>
                  </div>
                ))}
              </div>

              {/* --- Theme Settings (if custom) --- */}
              {profileData.theme === 'custom' && (
                <div className="form-section">
                  <h3>Custom Theme Settings</h3>

                  {renderContactRow('Background Type', profileData.themeSettings?.backgroundType)}
                  {renderContactRow('Background Color', profileData.themeSettings?.backgroundColor)}
                  {renderContactRow('Gradient Start', profileData.themeSettings?.gradientStart)}
                  {renderContactRow('Gradient End', profileData.themeSettings?.gradientEnd)}
                  {renderContactRow('Gradient Angle', profileData.themeSettings?.gradientAngle)}
                  {renderContactRow('Button Style', profileData.themeSettings?.buttonStyle)}
                  {renderContactRow('Button Color', profileData.themeSettings?.buttonColor)}
                  {renderContactRow('Button Text Color', profileData.themeSettings?.buttonFontColor)}
                  {renderContactRow('Font Family', profileData.themeSettings?.fontFamily)}
                </div>
              )}
            </div>
          )}
        </div>

        {/* RIGHT SIDE PREVIEW */}
        <aside className="user-preview-container">
          <div className="preview-frame">
            <LivePreview profile={profileData} />
          </div>
        </aside>
      </main>

      {/* QR CODE MODAL */}
      {showQRModal && (
        <div className="qr-modal-overlay" onClick={() => setShowQRModal(false)}>
          <div className="qr-modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Your Profile QR Code</h3>

            <div className="qr-canvas-container">
              <QRCodeCanvas
                id="qr-code-canvas"
                value={profileData.username ? window.location.origin + '/' + profileData.username : ''}
                size={256}
                bgColor={'#ffffff'}
                fgColor={'#000000'}
                level={'H'}
                includeMargin={true}
              />
            </div>

            <p style={{ marginTop: '15px', color: '#555' }}>
              Scan or use the button to save.
            </p>

            <div className="qr-modal-actions">
              <button onClick={downloadQR} className="action-button save-button">
                Download .PNG
              </button>

              <button
                onClick={() => setShowQRModal(false)}
                className="action-button preview-button"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* CROP MODAL */}
      {cropperOpen && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.7)',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px',
          }}
          onClick={cancelCrop}
        >
          <div
            style={{
              background: '#fff',
              borderRadius: 16,
              padding: 20,
              width: '100%',
              maxWidth: 480,
              boxShadow: '0 10px 30px rgba(0,0,0,0.3)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ marginBottom: 10 }}>Adjust Your Image</h3>

            <div
              style={{
                position: 'relative',
                width: '100%',
                height: 320,
                background: '#222',
                borderRadius: 12,
                overflow: 'hidden',
              }}
            >
              <Cropper
                image={rawImageSrc}
                crop={crop}
                zoom={zoom}
                aspect={aspect}
                cropShape={avatarShape === 'circle' ? 'round' : 'rect'}
                showGrid={false}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={onCropComplete}
                objectFit="contain"
              />
            </div>

            <div style={{ marginTop: 12 }}>
              <div>
                <label>Zoom: {zoom.toFixed(1)}x</label>
                <input
                  type="range"
                  min={1}
                  max={3}
                  step={0.1}
                  value={zoom}
                  onChange={(e) => setZoom(parseFloat(e.target.value))}
                  style={{ width: '100%' }}
                />
              </div>

              <div style={{ display: 'flex', gap: 8, marginTop: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                <span style={{ fontSize: 12, color: '#666' }}>Aspect:</span>
                <button type="button" className="action-button preview-button" onClick={() => setAspect(1)}>1:1</button>
                <button type="button" className="action-button preview-button" onClick={() => setAspect(4/5)}>4:5</button>
                <button type="button" className="action-button preview-button" onClick={() => setAspect(16/9)}>16:9</button>
              </div>

              <div style={{ display: 'flex', gap: 12, marginTop: 12, justifyContent: 'space-between' }}>
                <button
                  type="button"
                  className="action-button logout-button"
                  onClick={cancelCrop}
                >
                  Cancel
                </button>

                <button
                  type="button"
                  className="action-button save-button"
                  onClick={confirmCrop}
                >
                  Save Crop
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default UserDashboard;
